import React, { useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Stepper, Step, StepLabel, Typography, Button, StepContent, styled } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useContainer } from 'unstated-next';
import * as yup from 'yup';

import ContentSection from './ContentSection';
import FilesSection from './FilesSection';
import { StyledTypography } from './index.styles';
import TemplateContainer from './TemplateContext';
import ControlledInput from '../forms/ControlledInput';
import { StyledLoadingButton } from '../forms/LoadingButton';

const InnerContent = styled('div')`
    display: flex;
    flex-direction: column;
    max-width: 400px;
`;

const StepButtons = styled('span')`
    margin-top: 1rem;
    display: flex;

    button:first-of-type {
        margin-right: 0.5rem;
    }
`;

const InputContainer = styled('div')`
    display: flex;
    flex-direction: column;

    margin-bottom: 1rem;
    &:last-of-type {
        margin-bottom: 0;
    }
`;

const Footer = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
`;

const SubmitButton = styled(StyledLoadingButton)`
    margin-left: 0.5rem; ;
`;

const TemplateManagerCreateEdit: React.FC = () => {
    const { t } = useTranslation('templates');
    const [activeStep, setActiveStep] = useState(0);
    const history = useHistory();
    const { search } = useLocation();

    const {
        createTemplate,
        updateTemplate,
        createEditTemplateStatus,
        setTemplateEditId,
        templateToEdit,
        saveAsPublishedTemplate,
        saveAsDraftTemplate,
        createEditResponse,
        saveAsDraftTemplateStatus,
        saveAsPublishedTemplateStatus,
        publishedTemplates,
        draftTemplates
    } = useContainer(TemplateContainer);

    const { templateId } = useParams<{ templateId?: string }>();
    useEffect(() => {
        if (templateId) {
            const tempId = parseInt(templateId, 10);

            setTemplateEditId(tempId);
        } else {
            setTemplateEditId(null);
        }
    }, [setTemplateEditId, templateId]);

    const defaultValues = useMemo(
        () => ({
            templateName: '',
            description: ''
        }),
        []
    );

    const titleIsAlreadyInUse = (title: string): boolean => {
        const lowercaseTitle = title.toLocaleLowerCase();

        const publishedFound = publishedTemplates.find(
            (template) => template.class_name.toLocaleLowerCase() === lowercaseTitle
        );
        const draftFound = draftTemplates.find(
            (template) => template.class_name.toLocaleLowerCase() === lowercaseTitle
        );

        const templateWithSameTitle = draftFound ?? publishedFound;

        if (templateWithSameTitle && templateId) {
            // Did it just find the template being edited? if so, the template name can stay the same
            return templateWithSameTitle.sid !== parseInt(templateId, 10);
        }

        return Boolean(templateWithSameTitle);
    };

    const schema = yup.object({
        description: yup.string(),
        templateName: yup
            .string()
            .required(t('template-name-required'))
            .test({
                test(value) {
                    const isAlreadyInUse = titleIsAlreadyInUse(value as string);

                    if (isAlreadyInUse) {
                        return this.createError({
                            message: t('template-title-is-already-in-use'),
                            path: 'templateName'
                        });
                    }
                    return true;
                }
            })
    });

    const { control, getValues, reset, handleSubmit } = useForm({ defaultValues, resolver: yupResolver(schema) });

    useEffect(() => {
        if (templateToEdit) {
            reset({
                templateName: templateToEdit.class_name,
                description: templateToEdit.class_description ?? ''
            });
        } else {
            reset(defaultValues);
        }
    }, [reset, templateToEdit, defaultValues]);

    const goToNextStep = () => {
        setActiveStep((prev) => {
            if (prev !== 2) {
                return prev + 1;
            }

            return prev;
        });
    };

    const goToPreviousStep = () => {
        setActiveStep((prev) => {
            if (prev !== 0) {
                return prev - 1;
            }

            return prev;
        });
    };

    const handleCreateEditTemplate = async () => {
        const values = getValues();
        let status;

        if (templateToEdit) {
            status = await updateTemplate({
                sid: templateToEdit.sid,
                status: templateToEdit.status,
                ...values
            });
        } else {
            status = await createTemplate(values);
        }

        if (status === 'SUCCESS') {
            goToNextStep();
        }
    };

    const handlePublish = async () => {
        if (createEditResponse) {
            saveAsPublishedTemplate(createEditResponse.sid);
        }
    };

    const handleSaveAsDraft = async () => {
        if (createEditResponse) {
            saveAsDraftTemplate(createEditResponse.sid);
        }
    };

    const handleCancel = async () => {
        history.push(`/template-manager${search}`);
    };

    const editTemplateLoading = Boolean(templateId && !templateToEdit);

    const renderBackNextButtons = (step: number) => (
        <StepButtons>
            {step > 1 ? (
                <Button onClick={goToPreviousStep} variant="outlined">
                    {t('back')}
                </Button>
            ) : null}
            {step < 2 ? (
                <Button onClick={goToNextStep} variant="contained">
                    {t('next')}
                </Button>
            ) : null}
        </StepButtons>
    );

    return (
        <>
            <StyledTypography variant="h5">
                {templateId ? t('edit-template') : t('create-new-template')}
            </StyledTypography>

            <Stepper activeStep={activeStep} orientation="vertical">
                <Step key={0}>
                    <StepLabel>
                        <Typography variant="subtitle1">{t('settings')}</Typography>
                    </StepLabel>

                    <StepContent>
                        <InnerContent>
                            <InputContainer>
                                <ControlledInput
                                    isLoading={editTemplateLoading}
                                    placeholder={t('enter-template-title-here')}
                                    label={t('name-your-template')}
                                    helperText={t('you-can-rename-it-later')}
                                    control={control}
                                    name="templateName"
                                />
                            </InputContainer>
                            <InputContainer>
                                <ControlledInput
                                    isLoading={editTemplateLoading}
                                    label={t('description')}
                                    helperText={t('optional')}
                                    control={control}
                                    name="description"
                                />
                            </InputContainer>
                            <StepButtons>
                                <StyledLoadingButton
                                    loading={createEditTemplateStatus === 'LOADING'}
                                    onClick={handleSubmit(handleCreateEditTemplate)}
                                    variant="contained"
                                >
                                    {t('next')}
                                </StyledLoadingButton>
                            </StepButtons>
                        </InnerContent>
                    </StepContent>
                </Step>
                <Step key={1}>
                    <StepLabel>
                        <Typography variant="subtitle1">{t('files')}</Typography>
                    </StepLabel>

                    <StepContent>
                        <InnerContent>
                            <FilesSection />
                            {renderBackNextButtons(1)}
                        </InnerContent>
                    </StepContent>
                </Step>
                <Step key={2}>
                    <StepLabel>
                        <Typography variant="subtitle1">{t('content')}</Typography>
                    </StepLabel>

                    <StepContent>
                        <InnerContent>
                            <ContentSection />
                            {renderBackNextButtons(2)}
                        </InnerContent>
                    </StepContent>
                </Step>
            </Stepper>

            <Footer>
                <Button variant="outlined" onClick={handleCancel}>
                    {t('cancel')}
                </Button>
                <SubmitButton
                    loading={saveAsDraftTemplateStatus === 'LOADING'}
                    disabled={activeStep !== 2 || saveAsPublishedTemplateStatus === 'LOADING'}
                    variant="contained"
                    onClick={handleSaveAsDraft}
                >
                    {t('save-as-draft')}
                </SubmitButton>
                <SubmitButton
                    loading={saveAsPublishedTemplateStatus === 'LOADING'}
                    disabled={activeStep !== 2 || saveAsDraftTemplateStatus === 'LOADING'}
                    variant="contained"
                    onClick={handlePublish}
                >
                    {t('publish-template')}
                </SubmitButton>
            </Footer>
        </>
    );
};

export default TemplateManagerCreateEdit;
